import React, { useState, useEffect } from 'react';
import { Users, UserPlus, KeyRound, Power, Pencil, Check, Copy, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { usersAdminApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LoadingState } from '../../components/common/EmptyState';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'analista', label: 'Analista' },
  { value: 'revisor', label: 'Revisor' },
  { value: 'administrador_consorcio', label: 'Administrador Consorcio' },
  { value: 'operaciones', label: 'Operaciones' },
  { value: 'comercial', label: 'Comercial' },
];

const roleLabel = (r) => ROLES.find((x) => x.value === r)?.label || r;

const AdminUsuarios = () => {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [warnings, setWarnings] = useState({});
  const [loading, setLoading] = useState(true);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({ email: '', nombre: '', rol: 'analista' });

  // Edit dialog
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [editForm, setEditForm] = useState({ nombre: '', email: '', rol: '' });

  // Toggle active confirm
  const [toggleConfirm, setToggleConfirm] = useState(null);

  // Reset password confirm
  const [resetConfirm, setResetConfirm] = useState(null);

  // Temp password display
  const [tempPwdDisplay, setTempPwdDisplay] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await usersAdminApi.list();
      setItems(r.data.items || []);
      setWarnings(r.data.warnings || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // ============ Create ============
  const openCreate = () => {
    setCreateForm({ email: '', nombre: '', rol: 'analista' });
    setCreateOpen(true);
  };

  const submitCreate = async (e) => {
    e?.preventDefault();
    if (!createForm.email || !createForm.nombre || !createForm.rol) {
      toast.error('Completá todos los campos');
      return;
    }
    setCreating(true);
    try {
      const r = await usersAdminApi.create({
        email: createForm.email.trim().toLowerCase(),
        nombre: createForm.nombre.trim(),
        rol: createForm.rol,
      });
      setCreateOpen(false);
      toast.success('Usuario creado');
      if (r.data.temp_password) {
        setTempPwdDisplay({ pwd: r.data.temp_password, email: r.data.user.email, accion: 'creado' });
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error creando usuario');
    } finally {
      setCreating(false);
    }
  };

  // ============ Edit ============
  const openEdit = (u) => {
    setEditTarget(u);
    setEditForm({ nombre: u.nombre, email: u.email, rol: u.rol });
    setEditOpen(true);
  };

  const submitEdit = async (e) => {
    e?.preventDefault();
    const body = {};
    if (editForm.nombre.trim() !== editTarget.nombre) body.nombre = editForm.nombre.trim();
    if (editForm.email.trim().toLowerCase() !== editTarget.email) body.email = editForm.email.trim().toLowerCase();
    if (editForm.rol !== editTarget.rol) body.rol = editForm.rol;
    if (Object.keys(body).length === 0) {
      toast.info('No hay cambios para guardar');
      return;
    }
    setEditing(true);
    try {
      await usersAdminApi.update(editTarget.user_id, body);
      toast.success('Usuario actualizado');
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error actualizando');
    } finally {
      setEditing(false);
    }
  };

  // ============ Toggle active ============
  const doToggle = async () => {
    if (!toggleConfirm) return;
    try {
      await usersAdminApi.toggleActive(toggleConfirm.user_id, !toggleConfirm.activo);
      toast.success(toggleConfirm.activo ? 'Usuario desactivado' : 'Usuario activado');
      setToggleConfirm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
      setToggleConfirm(null);
    }
  };

  // ============ Reset password ============
  const doReset = async () => {
    if (!resetConfirm) return;
    try {
      const r = await usersAdminApi.resetPassword(resetConfirm.user_id);
      setTempPwdDisplay({ pwd: r.data.temp_password, email: resetConfirm.email, accion: 'reset' });
      setResetConfirm(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
      setResetConfirm(null);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles');
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8" data-testid="admin-usuarios-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Users className="w-6 h-6" /> Gestión de Usuarios Internos
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Administrá los accesos al sistema. Solo roles internos.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="btn-create-user">
          <UserPlus className="w-4 h-4 mr-2" /> Nuevo usuario
        </Button>
      </div>

      {warnings.single_admin && (
        <div className="mb-4 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="warning-single-admin">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-900">
            <p className="font-medium">Hay un solo admin activo</p>
            <p className="text-xs mt-0.5">Te recomendamos crear un segundo administrador para no perder acceso si algo le pasa a esa cuenta.</p>
          </div>
        </div>
      )}

      {loading ? (
        <LoadingState message="Cargando usuarios..." />
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Email</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Nombre</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Rol</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Estado</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Último login</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.user_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{u.email}</span>
                      {u.user_id === currentUser?.user_id && (
                        <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded">vos</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">{u.nombre}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-700 rounded capitalize">
                      {roleLabel(u.rol)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.activo ? (
                      <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded">Activo</span>
                    ) : (
                      <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded">Desactivado</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {u.last_login ? new Date(u.last_login).toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`btn-edit-${u.user_id}`}>
                        <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setResetConfirm(u)} data-testid={`btn-reset-${u.user_id}`}>
                        <KeyRound className="w-3.5 h-3.5 mr-1" /> Reset pwd
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setToggleConfirm(u)}
                        disabled={u.user_id === currentUser?.user_id}
                        title={u.user_id === currentUser?.user_id ? 'No podés desactivarte a vos mismo' : ''}
                        data-testid={`btn-toggle-${u.user_id}`}
                      >
                        <Power className="w-3.5 h-3.5 mr-1" /> {u.activo ? 'Desactivar' : 'Activar'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">Sin usuarios internos.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="dialog-create-user">
          <DialogHeader>
            <DialogTitle>Nuevo usuario interno</DialogTitle>
            <DialogDescription>Se generará una contraseña temporal que tendrás que copiar.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreate} className="space-y-4">
            <div>
              <Label htmlFor="c-email">Email</Label>
              <Input id="c-email" type="email" value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                data-testid="create-email" />
            </div>
            <div>
              <Label htmlFor="c-nombre">Nombre</Label>
              <Input id="c-nombre" value={createForm.nombre}
                onChange={(e) => setCreateForm({ ...createForm, nombre: e.target.value })}
                data-testid="create-nombre" />
            </div>
            <div>
              <Label htmlFor="c-rol">Rol</Label>
              <Select value={createForm.rol} onValueChange={(v) => setCreateForm({ ...createForm, rol: v })}>
                <SelectTrigger id="c-rol" data-testid="create-rol"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={creating} data-testid="create-submit">{creating ? 'Creando...' : 'Crear usuario'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="dialog-edit-user">
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
          </DialogHeader>
          {editTarget && (
            <form onSubmit={submitEdit} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  data-testid="edit-email" />
              </div>
              <div>
                <Label>Nombre</Label>
                <Input value={editForm.nombre}
                  onChange={(e) => setEditForm({ ...editForm, nombre: e.target.value })}
                  data-testid="edit-nombre" />
              </div>
              <div>
                <Label>Rol</Label>
                <Select value={editForm.rol} onValueChange={(v) => setEditForm({ ...editForm, rol: v })}>
                  <SelectTrigger data-testid="edit-rol"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                {editTarget.user_id === currentUser?.user_id && editForm.rol !== 'admin' && (
                  <p className="text-xs text-red-600 mt-1">No vas a poder cambiar tu propio rol admin desde acá.</p>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
                <Button type="submit" disabled={editing} data-testid="edit-submit">{editing ? 'Guardando...' : 'Guardar cambios'}</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Toggle confirm */}
      <AlertDialog open={!!toggleConfirm} onOpenChange={(o) => !o && setToggleConfirm(null)}>
        <AlertDialogContent data-testid="dialog-toggle-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{toggleConfirm?.activo ? 'Desactivar usuario' : 'Activar usuario'}</AlertDialogTitle>
            <AlertDialogDescription>
              {toggleConfirm?.activo
                ? `El usuario ${toggleConfirm?.email} no podrá iniciar sesión hasta que lo actives nuevamente.`
                : `El usuario ${toggleConfirm?.email} podrá iniciar sesión con su contraseña actual.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doToggle} data-testid="toggle-confirm-btn">Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset password confirm */}
      <AlertDialog open={!!resetConfirm} onOpenChange={(o) => !o && setResetConfirm(null)}>
        <AlertDialogContent data-testid="dialog-reset-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Resetear contraseña</AlertDialogTitle>
            <AlertDialogDescription>
              Se generará una contraseña temporal robusta para <strong>{resetConfirm?.email}</strong>.
              La contraseña actual quedará invalidada y la sesión activa se cerrará.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doReset} data-testid="reset-confirm-btn">Generar contraseña</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Temp password display */}
      <Dialog open={!!tempPwdDisplay} onOpenChange={(o) => !o && setTempPwdDisplay(null)}>
        <DialogContent data-testid="dialog-temp-pwd">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <Check className="w-5 h-5" /> Contraseña temporal generada
            </DialogTitle>
            <DialogDescription>
              Copiala ahora. Por seguridad, no la vamos a volver a mostrar.
            </DialogDescription>
          </DialogHeader>
          {tempPwdDisplay && (
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                Usuario: <span className="font-medium text-slate-900">{tempPwdDisplay.email}</span>
              </div>
              <div className="flex items-center gap-2 p-3 bg-slate-50 border border-slate-200 rounded font-mono text-sm">
                <span className="flex-1 select-all" data-testid="temp-pwd-value">{tempPwdDisplay.pwd}</span>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(tempPwdDisplay.pwd)} data-testid="temp-pwd-copy">
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                </Button>
              </div>
              <p className="text-xs text-amber-700">
                Pasale esta contraseña al usuario por un canal seguro. Debería cambiarla apenas inicie sesión.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setTempPwdDisplay(null)} data-testid="temp-pwd-close">Listo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsuarios;
